/**
 * TrendCurve — the dashboard's shared over-time line chart: monotone-
 * cubic curves, CWV threshold guide-lines, per-point hover tooltip,
 * deploy-marker ticks, and optional click-through on points that carry
 * a source-record id (e.g. an audit `aud_…`).
 *
 * Lives in its own module (rather than PerformanceSection, where it
 * grew up) because it's consumed by three otherwise-unrelated chunks —
 * the Performance overview, the per-page audit charts, and the variant
 * detail trend. When it lived inside PerformanceSection, that file and
 * AuditMetricCharts imported each other; the cycle was tolerated by
 * accident of chunk evaluation order until a third consumer flipped the
 * order and `TrendCurve` evaluated as `undefined` (#329 revert).
 */
import { monotoneSegment, monotoneTangents, smoothPath } from '@/lib/curve';
import { type MetricBand, bandFill } from '@/lib/format';
import type { DeployMarker } from '@/lib/queries/deploys';
import { cn } from '@/lib/utils';
import { Fragment, type ReactElement, useLayoutEffect, useRef, useState } from 'react';

/**
 * Structural spec `TrendCurve` consumes — the minimum it needs to draw a
 * line. `MetricSpec` + `LabMetricSpec` are assignable to it (their
 * required `goodThreshold`/`poorThreshold` satisfy the optional ones), so
 * the existing CWV + Lab charts pass their concrete specs through
 * unchanged. Specs that OMIT the thresholds (e.g. page-weight bytes,
 * third-party counts) render a plain neutral line with no dashed
 * threshold guides.
 */
export interface TrendCurveSpec {
  label: string;
  unit: string;
  band: (v: number | null | undefined) => MetricBand;
  format: (v: number | null | undefined) => string;
  /** Omit for neutral (no-threshold) metrics. */
  goodThreshold?: number;
  poorThreshold?: number;
}

/** One selectable record behind a bucketed point — e.g. an individual
 *  audit inside a day. When a point carries more than one, clicking it
 *  opens a picker instead of navigating. */
export interface PointItem {
  id: string;
  /** Primary row text, e.g. "14:32 · Mobile". */
  label: string;
  /** Muted trailing detail, e.g. the metric value "42". */
  detail?: string;
}

export interface TrendLine {
  key: string;
  color: string;
  points: { day: string; value: number | null; id?: string; t?: number; items?: PointItem[] }[];
  /**
   * Render as a faint solid reference line behind the primary curve —
   * the guest site in compare mode. Reference lines don't count toward
   * the band-colouring rule, so one primary + N references keeps the
   * primary's original band-coloured (green/amber/red) segments.
   */
  reference?: boolean;
}

/**
 * Guest-site series colour in site-compare mode — shared across every
 * compare surface (the faint reference line, tile footer swatches,
 * legend) so "the faded grey one is the other site" holds everywhere.
 * Muted rather than a competing hue: the comparison is context, the
 * home site's band-coloured line stays the headline.
 */
export const COMPARE_SERIES_COLOR = 'var(--color-mute)';

/**
 * Monotone-cubic interpolation (Fritsch–Carlson), the same algorithm as
 * d3's `curveMonotoneX`. Unlike Catmull-Rom it is *shape-preserving*:
 * the curve never overshoots between points, so peaks don't "fold over"
 * into uneven loops at the tips. Tangents depend on the whole series, so
 * we compute them once per line then build each segment as its own cubic
 * — keeping the per-segment paths the band-colouring relies on.
 */

/** One cubic Bézier segment from p1→p2 given the monotone tangents at
 *  each end. Used for the per-segment, band-coloured strokes. */

interface Pt {
  x: number;
  y: number;
  value: number;
  /** ISO day (YYYY-MM-DD) preserved from the trend row so the
   *  per-point tooltip can label the sample by date. */
  day: string;
  /** Optional source-row id (e.g. an audit `aud_…`) preserved so a
   *  clickable point can navigate to that record. Absent on the RUM +
   *  Lab trend lines, which aggregate per day. */
  id?: string;
  /** Epoch ms — the point's real moment, used for time-proportional x
   *  positioning under `timeAxis`. */
  t?: number;
  /** Records behind a bucketed point (audits in the day). >1 → the pip
   *  opens a picker on click instead of navigating. */
  items?: PointItem[];
}

/**
 * "May 25" — short month + numeric day. Used in the per-point
 * tooltip. Built once at module load via `Intl.DateTimeFormat` so
 * each render reuses the same formatter instance.
 */
const DAY_FORMATTER =
  typeof Intl !== 'undefined' && 'DateTimeFormat' in Intl
    ? new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' })
    : null;

function formatDay(iso: string): string {
  if (!DAY_FORMATTER) return iso;
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return DAY_FORMATTER.format(d);
}

/** Hover label for a deploy tick: what shipped + when. */
function deployTitle(d: DeployMarker): string {
  const what = d.branch || d.commit_sha?.slice(0, 7) || d.deploy_id || 'Deploy';
  return `Deploy · ${what} · ${new Date(d.created_at).toLocaleString()}`;
}

export function TrendCurve({
  lines,
  spec,
  deploys,
  previousPoints,
  onPointClick,
  timeAxis = false,
  itemNoun,
  height = 200,
}: {
  lines: TrendLine[];
  spec: TrendCurveSpec;
  /** Deploy markers within the window — ticks on the baseline. */
  deploys: DeployMarker[];
  /** Previous period's series — drawn as a dashed muted overlay,
   *  normalised across the same x-span as the current line. */
  previousPoints: { day: string; value: number | null }[];
  /** When set, points that carry an `id` become clickable (cursor +
   *  onClick → this handler). Used by the per-page audit charts to jump
   *  to a single audit; the RUM + Lab trend charts omit it (no ids). */
  onPointClick?: (id: string) => void;
  /** Position points by their real timestamp (`point.t`) within the
   *  chart's [min,max] time span, instead of evenly by index. Needed
   *  wherever points aren't evenly spaced in time — the per-page audit
   *  charts (irregular, sub-daily, and two sites with different audit
   *  counts must align by real moment, not ordinal position). Points
   *  without a `t` fall back to index spacing. */
  timeAxis?: boolean;
  /** What the records behind a bucketed point are called, singular and
   *  plural — "audit"/"audits" on the audit charts, "session"/"sessions"
   *  on the RUM trend. Only ever read for the count labels. */
  itemNoun?: { one: string; many: string };
  /**
   * Rendered chart height in px. The viewBox uses the SAME number (as it
   * does for the measured width), so one SVG unit is always one rendered
   * pixel — which is what lets the HTML overlay position by raw px.
   * Default suits a full-width card chart; the dense stack of audit
   * small-multiples passes a shorter one.
   */
  height?: number;
}): ReactElement {
  const noun = itemNoun ?? { one: 'audit', many: 'audits' };
  const countLabel = (n: number): string => `${n} ${n === 1 ? noun.one : noun.many}`;
  // The chart measures its own box so every coordinate below — SVG and
  // HTML overlay alike — is a REAL pixel. It used to draw into a nominal
  // 600-unit viewBox stretched to the card, which made each x-unit a
  // percentage of the card width: the y-axis gutter then ballooned to
  // ~90px of dead space on a wide screen while collapsing to ~20px on a
  // phone, exactly backwards from where the labels need room.
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(600);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = (): void => {
      const measured = Math.round(el.clientWidth);
      if (measured > 0) setWidth(measured);
    };
    // Measure SYNCHRONOUSLY first. A ResizeObserver's initial callback is
    // async, so relying on it alone paints one frame at the fallback width
    // — and since the SVG stretches, that frame draws a wrong-sized gutter
    // on every single load. Measuring here happens before paint.
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The resize listener is not redundant: observer delivery can be
    // throttled (it deferred for seconds in an embedded browser), which
    // otherwise leaves the viewBox stale at the old width after the card
    // has already reflowed to the new one.
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Chart box, in px. The left gutter holds the y-axis labels; a little
  // bottom pad leaves room for the deploy ticks.
  const padTop = 20;
  const padBottom = 24;
  const padLeft = 36;
  const padRight = 12;

  // Y scale with headroom past the poor threshold so the poor line + its
  // label sit clear of the bottom edge. Forcing the poor threshold into
  // range keeps the band lines visible even when the trend is all-good.
  // The fallback of 1 only guards an absent/all-zero series — it must NOT
  // act as a floor. CLS lives in 0–1, so flooring at 1 pinned its axis to
  // 1.10 and squashed a real 0.28 spike into the bottom quarter of the plot.
  const observedMax = Math.max(0, ...lines.flatMap((l) => l.points.map((p) => p.value ?? 0)));
  const dataMax = observedMax > 0 ? observedMax : 1;
  // Scores are a fixed 0–100 scale; CWV/timing with a poor threshold scale
  // to the data + headroom past that threshold; threshold-less (neutral)
  // metrics just scale to the data with a little headroom.
  const yMax =
    spec.unit === 'score'
      ? 100
      : spec.poorThreshold != null
        ? Math.max(dataMax * 1.1, spec.poorThreshold * 1.25)
        : dataMax * 1.1;

  // X stepping uses the longest line, on the assumption that the
  // perf-trend pipe emits day-aligned rows (it does, per the GROUP BY
  // day in pixel_site_perf_trend.pipe).
  const longest = lines.reduce((a, b) => (a.points.length >= b.points.length ? a : b), {
    points: [] as TrendLine['points'],
  } as TrendLine);
  const stepCount = Math.max(1, longest.points.length - 1);
  const xRange = width - padLeft - padRight;
  const yRange = height - padTop - padBottom;
  // Time-axis extent: the min/max real timestamp across every plotted
  // point. Both lines share it, so equal timestamps land at equal x —
  // that's what keeps two irregularly-sampled sites aligned by moment.
  let minT = Number.POSITIVE_INFINITY;
  let maxT = Number.NEGATIVE_INFINITY;
  if (timeAxis) {
    for (const l of lines) {
      for (const p of l.points) {
        if (p.value == null || p.t == null) continue;
        if (p.t < minT) minT = p.t;
        if (p.t > maxT) maxT = p.t;
      }
    }
  }
  const tSpan = maxT - minT;
  const xByIndex = (i: number): number => padLeft + (i / stepCount) * xRange;
  const xByTime = (t: number): number =>
    tSpan > 0 ? padLeft + ((t - minT) / tSpan) * xRange : padLeft + xRange / 2;
  const xAt = (i: number, t?: number): number =>
    timeAxis && t != null && Number.isFinite(tSpan) ? xByTime(t) : xByIndex(i);
  // Natural value-up axis: bigger value = higher on the chart. This
  // orients the bands correctly for either metric direction — for
  // lower-is-better metrics (LCP/INP/CLS…) the GOOD threshold is a small
  // value so green sits at the BOTTOM and red at the top; for
  // higher-is-better scores (Performance/SEO…) good is a large value so
  // green sits at the TOP and red at the bottom.
  const yAt = (v: number): number => padTop + (1 - v / yMax) * yRange;

  // Per-line XY coordinates with nulls stripped. Each line's points
  // are interpolated independently — gaps (e.g. a day with no
  // samples) collapse rather than producing an awkward "jump" in the
  // curve.
  const lineCoords = lines.map((line) => ({
    key: line.key,
    color: line.color,
    reference: line.reference ?? false,
    pts: line.points
      .map((p, i): Pt | null =>
        p.value == null
          ? null
          : {
              x: xAt(i, p.t),
              y: yAt(p.value),
              value: p.value,
              day: p.day,
              id: p.id,
              t: p.t,
              items: p.items,
            },
      )
      .filter((p): p is Pt => p !== null),
  }));

  // Single PRIMARY line → colour each segment by the band of its
  // destination point. Multiple primaries → keep the per-line distinct
  // form-factor colour (per-segment band-colouring would erase the
  // form-factor distinction the legend just promised). Reference lines
  // (faded dotted guests) don't count: the primary keeps its original
  // band-coloured look while a guest tracks quietly behind it.
  const useBandColours = lineCoords.filter((l) => !l.reference).length === 1;

  // Threshold guides are only drawn for specs that carry thresholds.
  // Neutral metrics (page-weight bytes, third-party counts) omit them and
  // render a plain band-coloured (here: always-neutral) line.
  const hasGood = spec.goodThreshold != null;
  const hasPoor = spec.poorThreshold != null;
  const thresholdY = hasGood ? yAt(spec.goodThreshold as number) : 0;
  const thresholdLabel = hasGood ? spec.format(spec.goodThreshold) : '';
  const poorY = hasPoor ? yAt(spec.poorThreshold as number) : 0;
  const poorLabel = hasPoor ? spec.format(spec.poorThreshold) : '';
  const baselineY = height - padBottom;

  // Deploy ticks: place each marker by time within the chart's data
  // span (time-proportional, not point-index, so it sits at its real
  // moment regardless of bucketing). Markers outside the span drop.
  const dayMs = (d: string) => Date.parse(`${d}T00:00:00Z`);
  // Under timeAxis the points are positioned against [minT,maxT], so the
  // deploy ticks must use the same span to line up; otherwise fall back
  // to the longest line's day range (the index-spaced default).
  const firstTime = timeAxis ? minT : longest.points.length > 0 ? dayMs(longest.points[0]!.day) : 0;
  const lastTime = timeAxis
    ? maxT
    : longest.points.length > 0
      ? dayMs(longest.points[longest.points.length - 1]!.day)
      : 0;
  const timeSpan = lastTime - firstTime;
  const deployTicks =
    timeSpan > 0
      ? deploys
          .map((d) => ({ d, t: Date.parse(d.created_at) }))
          .filter(({ t }) => Number.isFinite(t) && t >= firstTime && t <= lastTime)
          .map(({ d, t }) => ({ d, x: padLeft + ((t - firstTime) / timeSpan) * xRange }))
      : [];

  // Cluster deploys that land within ~2% of the chart width into one
  // pip (with a count) so a busy release day reads as a single marker
  // rather than a picket fence. Greedy left-to-right grouping by x.
  const deployClusters = (() => {
    if (deployTicks.length === 0) return [] as { x: number; deploys: DeployMarker[] }[];
    const gap = width * 0.02;
    const sorted = [...deployTicks].sort((a, b) => a.x - b.x);
    const out: { x: number; deploys: DeployMarker[] }[] = [];
    for (const { d, x } of sorted) {
      const last = out[out.length - 1];
      if (last && x - last.x <= gap) {
        last.deploys.push(d);
        last.x = (last.x * (last.deploys.length - 1) + x) / last.deploys.length;
      } else {
        out.push({ x, deploys: [d] });
      }
    }
    return out;
  })();

  // Previous-period coords — normalised across the full x-span (its own
  // index, independent of the current point count) so it overlays the
  // same window by relative time.
  const prevLen = previousPoints.length;
  const previousCoords = previousPoints
    .map((p, i) =>
      p.value == null
        ? null
        : { x: padLeft + (prevLen > 1 ? i / (prevLen - 1) : 0) * xRange, y: yAt(p.value) },
    )
    .filter((c): c is { x: number; y: number } => c !== null);

  // A soft area fill under each curve — closed to the baseline, a flat tint
  // of the line's colour. It rides in compare mode too: the guest line gets
  // its own, fainter fill (drawn behind) so the two areas stay distinct
  // while the strokes on top carry the real read. The HOME line keeps its
  // fill even under a previous-period overlay (the RUM trend always has
  // one, which left it as the only fill-less chart) — at 0.08 the muted
  // prior line stays legible through it. Only the guest's extra fill
  // yields to the overlay, so two soft washes never stack.
  const showGuestAreaFill = previousCoords.length < 2;
  const showAreaFill = true;

  // Hover state for the per-point tooltip. Tracked at the chart level
  // so a multi-line chart still shows one tooltip at a time — hovering
  // over a phone point lights up that point's tooltip and the desktop
  // dot stays in its idle "last value" state.
  const [hovered, setHovered] = useState<{ lineKey: string; index: number } | null>(null);
  const hoveredLine = hovered ? lineCoords.find((l) => l.key === hovered.lineKey) : null;
  const hoveredPt = hoveredLine?.pts[hovered?.index ?? -1] ?? null;
  // Independent hover for the deploy pips (index into deployClusters).
  const [hoveredDeploy, setHoveredDeploy] = useState<number | null>(null);
  // Open day-picker menu — set when a multi-audit pip is clicked. The
  // popover lists that day's records so the user can open a specific
  // one (a bucketed pip can't navigate to a single audit on its own).
  const [menu, setMenu] = useState<{ lineKey: string; index: number } | null>(null);
  const menuLine = menu ? lineCoords.find((l) => l.key === menu.lineKey) : null;
  const menuPt = menuLine?.pts[menu?.index ?? -1] ?? null;
  const menuItems = menuPt?.items ?? null;

  // Overlay positioning helper. The viewBox is sized in real pixels, so
  // an SVG x-unit IS an overlay px — this only exists to keep the call
  // sites reading as coordinates rather than raw template strings.
  const xPx = (x: number) => `${x}px`;

  return (
    // Relative wrapper (and the measured box): the SVG draws the curves
    // while a sibling HTML overlay carries every non-line element —
    // threshold labels, dots, tooltip. Those live in HTML so text and
    // circles get real font/border rendering instead of SVG glyphs.
    <div ref={wrapRef} className="relative animate-in fade-in duration-300">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Web Vitals over time"
        className="block w-full"
        style={{ height }}
      >
        <title>Web Vitals over time</title>

        {/* Background gridlines — four very subtle horizontal rules at
            20/40/60/80% of the plot height, drawn behind every curve so
            the eye can read a value off the y-axis without crowding the
            data. `non-scaling-stroke` keeps them hairline-thin under the
            SVG's horizontal stretch. */}
        {[0.2, 0.4, 0.6, 0.8].map((frac) => {
          const y = padTop + frac * (baselineY - padTop);
          return (
            <line
              key={frac}
              x1={padLeft}
              x2={width - padRight}
              y1={y}
              y2={y}
              stroke="var(--color-line)"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
              opacity="0.25"
            />
          );
        })}
        {/* Plot border — a subtle frame around the data area. The y-axis
            labels sit in the left gutter (left of padLeft), so they read
            as OUTSIDE this border; gridlines + curves sit inside. */}
        <rect
          x={padLeft}
          y={padTop}
          width={width - padLeft - padRight}
          height={baselineY - padTop}
          fill="none"
          stroke="var(--color-line)"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
          opacity="0.4"
        />

        {/* DASHED band thresholds (the "good" boundary tinted success,
            the "poor" boundary tinted warning) — the dashes denote
            "threshold" in the legend. The previous-period overlay below
            is SOLID so the two never read as the same thing.
            `non-scaling-stroke` keeps the dash cadence + width even under
            the SVG's horizontal stretch; they stop short of the right
            padding so the HTML pills have clean space to sit. */}
        {hasGood && (
          <line
            x1={padLeft}
            x2={width - padRight - 2}
            y1={thresholdY}
            y2={thresholdY}
            stroke="var(--color-success)"
            strokeWidth="1"
            strokeDasharray="3 4"
            vectorEffect="non-scaling-stroke"
            opacity="0.5"
          />
        )}
        {hasPoor && (
          <line
            x1={padLeft}
            x2={width - padRight - 2}
            y1={poorY}
            y2={poorY}
            stroke="var(--color-warning)"
            strokeWidth="1"
            strokeDasharray="3 4"
            vectorEffect="non-scaling-stroke"
            opacity="0.5"
          />
        )}

        {/* Curves. Each line's segments are rendered individually so
            a single line can pick up different colours per segment as
            it crosses the band thresholds — the orange-spike-on-green
            effect from the reference design. Each segment also drops
            an area fill closed to the baseline, painted with the
            matching gradient. The area sits BEHIND the stroke so the
            stroke stays crisp on top. */}
        {/* Previous-period comparison — SOLID muted line, drawn behind
            the current curve as a quiet reference. Solid so it's clearly
            distinct from the DASHED threshold lines. */}
        {previousCoords.length >= 2 && (
          <path
            d={smoothPath(previousCoords)}
            fill="none"
            stroke="var(--color-mute)"
            strokeWidth="1.25"
            strokeLinecap="round"
            opacity="0.55"
            vectorEffect="non-scaling-stroke"
          />
        )}

        {/* Reference lines (guest site in compare mode) — a faint SOLID
            line, drawn before (= behind) the primary curves. Safe to be
            solid: the previous-period overlay (also solid muted) is
            suppressed while comparing, so the two never co-exist. Low
            opacity keeps it a quiet backdrop — the home site's
            band-coloured line stays the headline. One smooth path, no
            band segments: a reference tracks, it doesn't judge. */}
        {lineCoords
          .filter((l) => l.reference)
          .map((line) =>
            line.pts.length < 2 ? (
              <g key={line.key} />
            ) : (
              <g key={line.key}>
                {/* Guest's own faint area — fainter than the home fill and
                    drawn behind everything, so it reads as a quiet backdrop
                    the home curve sits in front of. */}
                {showGuestAreaFill && (
                  <path
                    d={`${smoothPath(line.pts)} L${(line.pts[line.pts.length - 1] as Pt).x.toFixed(1)},${baselineY.toFixed(1)} L${(line.pts[0] as Pt).x.toFixed(1)},${baselineY.toFixed(1)} Z`}
                    fill={line.color}
                    fillOpacity={0.05}
                    stroke="none"
                  />
                )}
                <path
                  d={smoothPath(line.pts)}
                  fill="none"
                  stroke={line.color}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                  opacity="0.4"
                />
              </g>
            ),
          )}

        {lineCoords
          .filter((l) => !l.reference)
          .map((line) => {
            if (line.pts.length < 2) return <g key={line.key} />;
            // Tangents depend on the whole line — compute once, reuse for
            // every stroke segment. Monotone → no overshoot at the peaks.
            const tangents = monotoneTangents(line.pts);
            const last = line.pts[line.pts.length - 1] as Pt;
            const first = line.pts[0] as Pt;
            return (
              <g key={line.key}>
                {/* Soft area under the curve — the smooth top edge (same
                    monotone path as the stroke) closed down to the baseline
                    and back, filled with a faint tint of the line's band. */}
                {showAreaFill && (
                  <path
                    d={`${smoothPath(line.pts)} L${last.x.toFixed(1)},${baselineY.toFixed(1)} L${first.x.toFixed(1)},${baselineY.toFixed(1)} Z`}
                    fill={useBandColours ? bandFill(spec.band(last.value)) : line.color}
                    fillOpacity={0.08}
                    stroke="none"
                  />
                )}
                {/* Band-coloured strokes, one per segment. `non-scaling-stroke`
                  renders the width + round caps in screen space, so the
                  SVG's non-uniform stretch can't skew the caps or vary the
                  stroke width across date ranges. No area fill — the band
                  thresholds are shown as dashed reference lines instead. */}
                {line.pts.slice(0, -1).map((p1, i) => {
                  const p2 = line.pts[i + 1] as Pt;
                  const d = monotoneSegment(p1, p2, tangents[i]!, tangents[i + 1]!);
                  const colour = useBandColours ? bandFill(spec.band(p2.value)) : line.color;
                  return (
                    <path
                      // biome-ignore lint/suspicious/noArrayIndexKey: segments are positional + count-stable
                      key={i}
                      d={d}
                      fill="none"
                      stroke={colour}
                      strokeWidth="1.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  );
                })}
              </g>
            );
          })}
      </svg>

      {/* Overlay layer. `inset-0` matches the SVG bounds exactly so
          % left + px top map straight onto SVG coordinates (the SVG
          renders at exactly `height` px, so its y-units equal pixels
          here).
          Dots + threshold pill stay round / un-stretched because
          they're plain HTML, not SVG. */}
      <div className="pointer-events-none absolute inset-0">
        {/* Legend (top-right): one dashed swatch denoting the band
            THRESHOLDS (the green good + amber poor lines, shown as a
            two-tone dash) — not labelled per-band — and, when drawn, a
            SOLID swatch for the previous-period overlay. The two line
            styles (dashed vs solid) are spelled out so they can't be
            confused. */}
        <div className="absolute top-0 right-0 flex items-center gap-2.5 text-[11px] text-[var(--color-mute)]">
          {/* Per-line swatches — only when there's more than one line
              (site-compare mode), where colour is the only way to tell
              the series apart. Single-line charts skip it: the line is
              band-coloured there, so one swatch would be a lie. */}
          {lineCoords.length > 1 &&
            lineCoords.map((l) => {
              // The primary line is band-coloured (its `color` is only a
              // fallback), so a swatch in `l.color` would mis-state what's
              // drawn — blue chip beside a green line. Match the swatch to
              // the line's last-point band colour (= the end-dot colour)
              // when band-colouring is active; reference lines keep their
              // literal faint colour.
              const last = l.pts[l.pts.length - 1];
              const swatch =
                useBandColours && !l.reference && last ? bandFill(spec.band(last.value)) : l.color;
              return (
                <span key={l.key} className="flex max-w-40 items-center gap-1">
                  <span
                    aria-hidden="true"
                    className={cn(
                      'inline-block w-3 shrink-0',
                      // Faint thin swatch mirrors the reference line's
                      // stroke so the legend teaches the encoding.
                      l.reference ? 'border-t opacity-40' : 'border-t-2',
                    )}
                    style={{ borderColor: swatch }}
                  />
                  <span className="truncate">{l.key}</span>
                </span>
              );
            })}
          {(hasGood || hasPoor) && (
            <span className="flex items-center gap-1">
              <span aria-hidden="true" className="inline-flex">
                <span className="inline-block w-1.5 border-[var(--color-success)] border-t border-dashed" />
                <span className="inline-block w-1.5 border-[var(--color-warning)] border-t border-dashed" />
              </span>
              Thresholds
            </span>
          )}
          {previousCoords.length >= 2 && (
            <span className="flex items-center gap-1">
              <span
                aria-hidden="true"
                className="inline-block w-3 border-[var(--color-mute)] border-t"
              />
              Previous period
            </span>
          )}
        </div>
        {/* Compact y-axis in the tight left gutter (right-aligned).
            Value-up scale → scale max at top, 0 at bottom. The good /
            poor markers (success dot / amber triangle) land by value, so
            their vertical order follows the metric's direction. Rendered
            as HTML so the glyphs stay round/sharp and the text isn't
            squished by the SVG stretch. */}
        <div
          className="absolute -translate-y-1/2 pr-1 text-right text-[11px] tabular-nums text-[var(--color-mute)]/55"
          style={{ top: `${padTop}px`, left: 0, width: xPx(padLeft) }}
        >
          {spec.format(yMax)}
        </div>
        {hasGood && (
          <div
            className="absolute flex -translate-y-1/2 items-center justify-end gap-1 pr-1 text-[11px] font-medium tabular-nums text-[var(--color-mute)]"
            style={{ top: `${thresholdY}px`, left: 0, width: xPx(padLeft) }}
          >
            {thresholdLabel}
            <span className="leading-none text-[var(--color-success)]">●</span>
          </div>
        )}
        {hasPoor && (
          <div
            className="absolute flex -translate-y-1/2 items-center justify-end gap-1 pr-1 text-[11px] font-medium tabular-nums text-[var(--color-mute)]"
            style={{ top: `${poorY}px`, left: 0, width: xPx(padLeft) }}
          >
            {poorLabel}
            <span className="text-[9px] leading-none text-[var(--color-warning)]">▲</span>
          </div>
        )}
        {/* Zero at the bottom of the value-up axis. */}
        <div
          className="absolute -translate-y-1/2 pr-1 text-right text-[11px] tabular-nums text-[var(--color-mute)]/55"
          style={{ top: `${baselineY}px`, left: 0, width: xPx(padLeft) }}
        >
          {spec.unit === 'cls' ? '0.00' : '0'}
        </div>

        {/* Deploy pips along the baseline — clustered, time-positioned.
            On hover: brighten, raise a faint vertical guide up through
            the curve, and show a tooltip (single deploy details, or an
            "N deploys" count for a cluster). Subtle but tied to the
            curve so a regression can be read against a release. */}
        {deployClusters.map((cluster, i) => {
          const isHovered = hoveredDeploy === i;
          const count = cluster.deploys.length;
          const first = cluster.deploys[0]!;
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: clusters are positional + stable per render
            <Fragment key={`deploy-${i}`}>
              {isHovered && (
                <div
                  aria-hidden="true"
                  className="absolute w-px -translate-x-1/2 bg-[var(--color-accent-brand)]/30"
                  style={{
                    left: xPx(cluster.x),
                    top: `${padTop}px`,
                    height: `${baselineY - padTop}px`,
                  }}
                />
              )}
              <div
                aria-hidden="true"
                className={cn(
                  '-translate-x-1/2 absolute rounded-full transition-colors',
                  isHovered ? 'bg-[var(--color-accent-brand)]' : 'bg-[var(--color-ink-soft)]/45',
                )}
                style={{
                  left: xPx(cluster.x),
                  bottom: `${padBottom}px`,
                  height: count > 1 ? 14 : 11,
                  width: count > 1 ? 3 : 1.5,
                }}
              />
              <button
                type="button"
                aria-label={count > 1 ? `${count} deploys` : deployTitle(first)}
                className="-translate-x-1/2 pointer-events-auto absolute w-4 cursor-default outline-none"
                style={{ left: xPx(cluster.x), bottom: `${padBottom}px`, height: 18 }}
                onMouseEnter={() => setHoveredDeploy(i)}
                onMouseLeave={() => setHoveredDeploy(null)}
                onFocus={() => setHoveredDeploy(i)}
                onBlur={() => setHoveredDeploy(null)}
              />
              {isHovered && (
                <div
                  className="-translate-x-1/2 -translate-y-full pointer-events-none absolute z-10 whitespace-nowrap rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2 py-1 text-[10px] text-[var(--color-ink-soft)] shadow-[var(--shadow-popover)]"
                  style={{ left: xPx(cluster.x), top: `${baselineY - 4}px` }}
                >
                  {count > 1 ? `${count} deploys` : deployTitle(first)}
                </div>
              )}
            </Fragment>
          );
        })}

        {/* Per-line points: an invisible hit-area at every sample
            for the tooltip, plus a visible dot at the last sample
            of each line (always) and at any currently-hovered
            sample. Halo + solid dot are two divs because CSS vars
            can't carry alpha — using opacity on the outer ring
            keeps the colour chain consistent. */}
        {lineCoords.map((line) => (
          <Fragment key={line.key}>
            {line.pts.map((p, i) => {
              // Reference lines stay hoverable (the tooltip is how you
              // read the guest's numbers) but skip the idle last-point
              // dot — a resting dot would promote the background line.
              const isLast = i === line.pts.length - 1 && !line.reference;
              const isHovered = hovered?.lineKey === line.key && hovered.index === i;
              const colour =
                useBandColours && !line.reference ? bandFill(spec.band(p.value)) : line.color;
              // A bucketed point with >1 record opens a picker; a single
              // record (or a bare `id`) navigates straight through.
              const multi = (p.items?.length ?? 0) > 1;
              const singleId = multi ? undefined : (p.items?.[0]?.id ?? p.id);
              const clickable = !!onPointClick && (multi || !!singleId);
              return (
                <Fragment key={`${line.key}-${i}`}>
                  {(isLast || isHovered) && (
                    <>
                      <span
                        aria-hidden="true"
                        className="absolute size-4 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-25"
                        style={{ left: xPx(p.x), top: `${p.y}px`, background: colour }}
                      />
                      <span
                        aria-hidden="true"
                        className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                        style={{ left: xPx(p.x), top: `${p.y}px`, background: colour }}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    aria-label={
                      multi
                        ? `${formatDay(p.day)}, ${countLabel(p.items!.length)}`
                        : `${formatDay(p.day)}, ${spec.format(p.value)}`
                    }
                    className={cn(
                      'pointer-events-auto absolute size-6 -translate-x-1/2 -translate-y-1/2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-brand)]/40',
                      clickable ? 'cursor-pointer' : 'cursor-default',
                    )}
                    style={{ left: xPx(p.x), top: `${p.y}px` }}
                    onClick={
                      clickable
                        ? () => {
                            if (multi) setMenu({ lineKey: line.key, index: i });
                            else if (singleId) onPointClick!(singleId);
                          }
                        : undefined
                    }
                    onMouseEnter={() => setHovered({ lineKey: line.key, index: i })}
                    onMouseLeave={() => setHovered(null)}
                    onFocus={() => setHovered({ lineKey: line.key, index: i })}
                    onBlur={() => setHovered(null)}
                  />
                </Fragment>
              );
            })}
          </Fragment>
        ))}

        {/* Tooltip — single element that follows the hovered point.
            Anchored above the dot via -translate-y; we don't flip
            it to below near the top edge because the chart's y
            padding (padTop) keeps every point that many pixels clear
            of the top, which is more than the tooltip's height. */}
        {hoveredLine && hoveredPt && !menu && (
          <div
            className="absolute -translate-x-1/2 -translate-y-[140%] whitespace-nowrap rounded-md border border-[var(--color-line)] bg-[var(--color-paper-alt)] px-2 py-1 text-[10px] font-medium tabular-nums text-[var(--color-ink-soft)] shadow-[var(--shadow-popover)]"
            style={{ left: xPx(hoveredPt.x), top: `${hoveredPt.y}px` }}
          >
            <span className="text-[var(--color-mute)]">{formatDay(hoveredPt.day)}</span>
            {' · '}
            {spec.format(hoveredPt.value)}
            {lineCoords.length > 1 && (
              <span className="text-[var(--color-mute)]">{` · ${capitalize(hoveredLine.key)}`}</span>
            )}
            {(hoveredPt.items?.length ?? 0) > 1 && (
              <span className="text-[var(--color-mute)]">{` · ${countLabel(hoveredPt.items!.length)} — click`}</span>
            )}
          </div>
        )}

        {/* Day-picker popover — opens on clicking a bucketed pip that
            holds more than one audit. A backdrop catches the click-away;
            the list scrolls so a day with dozens of runs stays usable. */}
        {menuPt && menuItems && menuItems.length > 0 && (
          <>
            <button
              type="button"
              aria-label="Close"
              className="pointer-events-auto fixed inset-0 z-20 cursor-default"
              onClick={() => setMenu(null)}
            />
            <div
              className={cn(
                'pointer-events-auto absolute z-30 w-56 overflow-hidden rounded-lg border border-[var(--color-line)] bg-[var(--color-paper)] shadow-[var(--shadow-popover)]',
                menuPt.x / width < 0.33
                  ? 'translate-x-0'
                  : menuPt.x / width > 0.66
                    ? '-translate-x-full'
                    : '-translate-x-1/2',
              )}
              style={{ left: xPx(menuPt.x), top: `${menuPt.y + 8}px` }}
            >
              <div className="flex items-center justify-between gap-2 border-[var(--color-line-soft)] border-b px-2.5 py-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  {formatDay(menuPt.day)}
                </span>
                <span className="text-[10px] text-[var(--color-mute)]">
                  {countLabel(menuItems.length)}
                </span>
              </div>
              <div className="max-h-56 overflow-y-auto py-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 px-2.5 py-1.5 text-left text-xs text-[var(--color-ink-soft)] outline-none transition-colors hover:bg-[var(--color-paper-alt)] focus-visible:bg-[var(--color-paper-alt)]"
                    onClick={() => {
                      setMenu(null);
                      onPointClick?.(item.id);
                    }}
                  >
                    <span className="truncate">{item.label}</span>
                    {item.detail != null && (
                      <span className="shrink-0 font-semibold tabular-nums text-[var(--color-mute)]">
                        {item.detail}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
